import React, { memo, useRef, useEffect } from "react";

/**
 * Props for the TextAreaWithCounter component
 */
interface TextAreaWithCounterProps {
  /** Unique identifier for the textarea */
  id: string;
  /** Label text displayed above the textarea */
  label?: string;
  /** Current value of the textarea */
  value: string;
  /** Placeholder text when textarea is empty */
  placeholder?: string;
  /** Maximum character length allowed */
  maxLength?: number;
  /** ARIA label for accessibility */
  ariaLabel?: string;
  /** Additional CSS classes for the textarea */
  className?: string;
  /** Additional CSS classes for the wrapper */
  wrapperClassName?: string;
  /** Callback fired when value changes */
  onChange: (value: string) => void;
  /** Number of lines to show (data-show-lines attribute) */
  showLines?: number;
  /** Whether autocomplete is enabled */
  autoComplete?: "on" | "off";
  /** Whether the textarea is read-only */
  readOnly?: boolean;
  isRequired?: boolean;
}

/**
 * Reusable textarea component with character counter
 * Follows design system specifications with vessel theme
 * In readOnly mode, preserves newline formatting from the value string
 * 
 * @example
 * ```tsx
 * <TextAreaWithCounter
 *   id="notes-1"
 *   label="Notes"
 *   value={notes}
 *   placeholder="Enter notes"
 *   maxLength={2048}
 *   onChange={(value) => setNotes(value)}
 * />
 * 
 * // ReadOnly mode preserves \n characters
 * <TextAreaWithCounter
 *   id="notes-2"
 *   value="Line 1\nLine 2"
 *   readOnly={true}
 *   onChange={noop}
 * />
 * ```
 */
const TextAreaWithCounter: React.FC<TextAreaWithCounterProps> = memo(({
  id,
  label,
  value,
  placeholder = "Enter text",
  maxLength = 2048,
  ariaLabel,
  className = "",
  wrapperClassName = "",
  onChange,
  showLines = 5,
  autoComplete = "on",
  readOnly = false,
  isRequired = false,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Set initial width to match wrapper on first render
  useEffect(() => {
    if (textareaRef.current && wrapperRef.current) {
      const wrapperWidth = wrapperRef.current.offsetWidth;
      textareaRef.current.style.width = `${wrapperWidth}px`;
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!readOnly) {
      const newValue = e.target.value.slice(0, maxLength);
      onChange(newValue);
    }
  };

  const charactersLeft = maxLength - value.length;
  const counterId = `${id}-counter`;

  // If read-only, show simpler view with preserved newlines
  if (readOnly) {
    return (
      <div className="flex flex-col gap-2 w-full max-w-full">
        <div className="text-[16px] text-gray-700 whitespace-pre-line">
          {value || <span className="text-gray-400 italic">No notes</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 w-dvw max-w-full">
      <div
        className="ds-form"
        data-ds-theme="vessel"
        data-ds-variant="text-area"
        data-ds-version="1"
      >
        {label && <label htmlFor={id}>{label}</label>}
        <div ref={wrapperRef} className={`textarea-wrapper w-full! ${wrapperClassName}`}>
          <textarea
            ref={textareaRef}
            autoComplete={autoComplete}
            id={id}
            className={`ds-form__text-area_global text-area_word-count max-w-full resize box-border! bg-white! text-[#0D1C3D]! ${className}`}
            data-show-lines={showLines.toString()}
            name=""
            maxLength={maxLength}
            value={value}
            placeholder={placeholder}
            aria-label={ariaLabel || label}
            aria-describedby={counterId}
            onChange={handleChange}
          />
          <div className="character-count my-0! mt-1!">
            <span
              className="count-area ds-form__text-input-helper"
              id={counterId}
              aria-live="polite"
              aria-atomic="true"
              data-description={` of ${maxLength} characters left`}
              data-full-text={`No more characters available, ${maxLength} of ${maxLength}`}
            >
              {isRequired ? (<span className="mr-4!"> Required Field   </span>) : ""}{charactersLeft} of {maxLength} characters left
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

TextAreaWithCounter.displayName = "TextAreaWithCounter";

export default TextAreaWithCounter;
